import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Separator } from "design-system/atoms/Separator"
import { GripVertical, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Fragment } from "react/jsx-runtime"
import { SearchCatalogBoost } from "schema"

type BoostEntry = SearchCatalogBoost[number]

type Props = {
  entries: SearchCatalogBoost
  getLabel: (entry: BoostEntry) => string
  onChange: (entries: SearchCatalogBoost) => void
}

const getEntryId = (entry: BoostEntry) => `${entry.type}:${entry.value}`

export function CatalogBoostList({ entries, getLabel, onChange }: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return

    const activeIndex = entries.findIndex((entry) => getEntryId(entry) === active.id)
    const overIndex = entries.findIndex((entry) => getEntryId(entry) === over.id)
    if (activeIndex === -1 || overIndex === -1) return

    onChange(arrayMove(entries, activeIndex, overIndex))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={entries.map(getEntryId)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col border rounded-md overflow-hidden">
          {entries.map((entry, index) => (
            <Fragment key={getEntryId(entry)}>
              <BoostRow
                entry={entry}
                position={index + 1}
                label={getLabel(entry)}
                onRemove={() => onChange(entries.filter((other) => other !== entry))}
              />
              {index < entries.length - 1 && <Separator />}
            </Fragment>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

type BoostRowProps = {
  entry: BoostEntry
  position: number
  label: string
  onRemove: () => void
}

function BoostRow({ entry, position, label, onRemove }: BoostRowProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: getEntryId(entry),
  })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`flex items-center gap-2 bg-card p-1 ${isDragging ? "z-10" : ""}`}
    >
      <Button
        {...attributes}
        {...listeners}
        type="button"
        variant="link"
        size="icon"
        className="h-8 w-8 shrink-0 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </Button>
      <span className="w-4 text-sm text-muted-foreground">{position}</span>
      <span className="flex-1 truncate text-sm">{label}</span>
      <Badge variant="outline">
        {t(`capabilities.catalog.SearchCatalog.config.productSelection.boost.types.${entry.type}`)}
      </Badge>
      <Button
        type="button"
        variant="link"
        size="icon"
        className="h-8 w-8 shrink-0"
        aria-label={t("capabilities.catalog.SearchCatalog.config.productSelection.boost.remove")}
        onClick={onRemove}
      >
        <X className="size-4" />
      </Button>
    </div>
  )
}
